import { RadioGroup } from "@headlessui/react";
import { useState } from "react";
import SubscriptionFormContext, {
  SubscriptionFormContextType,
} from "./SubscriptionFormContext";
import PurchaseTypeOption from "./PurchaseTypeOption";
import FrequencySelector from "./FrequencySelector";
import BenefitsList from "./BenefitsList";
import RefillPreferenceSelector from "./RefillPreferenceSelector";
import CartAPIForm from "./CartAPIForm";
import BuyButton from "./BuyButton";
import { addToCart, resolveVariantSelection } from "./cartApi";

export const SubscriptionFormConfig = {
  key: "subscription-form",
  component: SubscriptionForm,
};

export default function SubscriptionForm(
  subscriptionFormContext: SubscriptionFormContextType
) {
  const {
    labels: { oneTimePurchase, subscription },
    prices: { basePrice, subscriptionPrice },
    frequencies,
    productIds,
  } = subscriptionFormContext;
  const [isSubscribing, setIsSubscribing] = useState(true);
  const [sellingPlanId, setSellingPlanId] = useState<string>(
    frequencies[0].sellingPlanId
  );
  const [isSubscribingToRefills, setIsSubscribingToRefills] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [isAddingToCart, setIsAddingToCart] = useState(false);
  const [addToCartError, setAddToCartError] = useState<string | null>(null);

  async function handleAddToCart() {
    setIsAddingToCart(true);
    setAddToCartError(null);
    try {
      const { id, sellingPlan } = resolveVariantSelection({
        isSubscribing,
        isSubscribingToRefills,
        sellingPlanId,
        productIds,
      });
      await addToCart({ id, sellingPlan, quantity });
    } catch (error) {
      setAddToCartError(
        error instanceof Error ? error.message : "Failed to add to cart"
      );
    } finally {
      setIsAddingToCart(false);
    }
  }

  return (
    <SubscriptionFormContext.Provider value={subscriptionFormContext}>
      <div className="xflex xflex-col xitems-center xgap-4 md:xgap-6 xw-full">
        <div className="xflex xflex-col xitems-center xgap-2 md:xgap-4 xw-full">
          <RadioGroup
            value={isSubscribing}
            onChange={setIsSubscribing}
            aria-label="Purchase type"
            className="xflex xflex-col xgap-2 md:xgap-4 xw-full"
          >
            <PurchaseTypeOption
              label={oneTimePurchase}
              price={basePrice}
              value={false}
            />
            <PurchaseTypeOption
              compareAtPrice={basePrice}
              highlight={true}
              label={subscription}
              price={subscriptionPrice}
              value={true}
            />
          </RadioGroup>
          {isSubscribing && (
            <div
              className="
            xflex xflex-col xgap-4
            xw-[95%] xh-128
            xborder-4 xborder-dark-green xrounded-3xl
            xpx-6 md:xpx-8 xpy-4 md:xpy-6
          "
            >
              <FrequencySelector
                sellingPlanId={sellingPlanId}
                setSellingPlanId={setSellingPlanId}
              />
              <BenefitsList />
              <RefillPreferenceSelector
                isSubscribingToRefills={isSubscribingToRefills}
                onIsSubscribingToRefillsChange={setIsSubscribingToRefills}
              />
            </div>
          )}
          <CartAPIForm
            isSubscribing={isSubscribing}
            sellingPlanId={sellingPlanId}
            isSubscribingToRefills={isSubscribingToRefills}
            quantity={quantity}
          />
        </div>
        {addToCartError && (
          <p className="xtext-red-600 xtext-sm" role="alert">
            {addToCartError}
          </p>
        )}
        <BuyButton
          quantity={quantity}
          setQuantity={setQuantity}
          onAddToCart={handleAddToCart}
          isAddingToCart={isAddingToCart}
        />
      </div>
    </SubscriptionFormContext.Provider>
  );
}
